// Opening a saved PR on Create PR (loadPRById, used by ?editPR=, the First / Previous / Next / Last
// buttons, Find and "Find a PR"): the page shows and keeps what the PR was saved with. The new-PR
// defaults (required date today + 7, the approval flow auto-fill), the approval flow re-check and the
// header project's line values react to what the user changes, not to the load, and the draft
// autosave sends only what changes after it. Until Nov 2026 the flow options, loaded at the same
// time, could land after the PR and reset its required date, and autosave saved that.

/** The header fields an approval flow fills. */
export type PrHeaderFields = {
  requisitionType: string
  allocationType: string
  allocationCode: string
  facilityType: string
  cycle: string
}

/** The header fields as one value, to tell whether the header is still the one a PR was opened with. */
export const prHeaderKey = (h: PrHeaderFields): string =>
  JSON.stringify([h.requisitionType, h.allocationType, h.allocationCode, h.facilityType, h.cycle])

/**
 * Resolves once no saved PR is being loaded. `currentLoad` returns the load in progress, or null.
 * A load started while waiting is waited for too; a failed load counts as finished.
 */
export async function settledLoads(currentLoad: () => Promise<unknown> | null): Promise<void> {
  for (let load = currentLoad(); load; load = currentLoad()) {
    try {
      await load
    } catch {
      // A failed load leaves the page as it was.
    }
    if (currentLoad() === load) return
  }
}

/**
 * Whether the new-PR defaults may fill the header: only once no saved PR is being loaded and none
 * is shown (`shownPr` returns null for a new PR).
 */
export async function newPrDefaultsAllowed(
  currentLoad: () => Promise<unknown> | null,
  shownPr: () => unknown
): Promise<boolean> {
  await settledLoads(currentLoad)
  return shownPr() === null
}

/** A PR line as the draft autosave reads it. */
export type DraftLine = {
  itemtype: string
  itemcode?: string
  itemdescription?: string
  u_item_details?: string
  quantity: number
  uomcode?: string
  unitprice: number
  accountcode: string
  vendorcode?: string
  warehouse?: string
  projectcode?: string
  dim1?: string
  dim2?: string
  dim3?: string
  dim4?: string
  dim5?: string
  deliverydate?: string
  u_ser_qty?: number | null
  u_ser_unit_price?: number | null
}

/** The header as the draft autosave reads it. */
export type DraftHeader = PrHeaderFields & { requiredDate: string; remarks: string }

/** The body of the draft autosave (PUT /pr/draft/:id): the header and the lines with an item code or a description. */
export const draftAutosavePayload = (h: DraftHeader, lines: DraftLine[]) => ({
  requireddate: h.requiredDate || null,
  requisitiontype: h.requisitionType,
  allocationtype: h.allocationType,
  allocationcode: h.allocationCode || null,
  facilitytype: h.facilityType,
  cycle: h.cycle,
  remarks: h.remarks,
  lines: lines.filter(l => !!l.itemcode || !!l.itemdescription).map(l => ({ itemcode: l.itemcode || null, itemdescription: l.itemdescription || '', u_item_details: l.u_item_details || '', quantity: l.quantity, uomcode: l.uomcode || undefined, unitprice: l.unitprice, accountcode: l.accountcode, vendorcode: l.vendorcode || undefined, warehouse: l.warehouse || undefined, projectcode: l.projectcode || undefined, dim1: l.dim1 || undefined, dim2: l.dim2 || undefined, dim3: l.dim3 || undefined, dim4: l.dim4 || undefined, dim5: l.dim5 || undefined, deliverydate: l.deliverydate ? l.deliverydate : undefined, u_ser_qty: l.u_ser_qty ?? null, u_ser_unit_price: l.u_ser_unit_price ?? null, itemtype: l.itemtype }) )
})

/** A draft change still waiting for its autosave (sent 1.5 s after the last edit). */
export type WaitingAutosave = { prId: number; payload: ReturnType<typeof draftAutosavePayload>; body: string }

/**
 * What becomes of a waiting autosave when the page changes. 'keep': the same draft is still being edited
 * (it is rescheduled). 'send' it now: the page moved to another PR, to Find or to a read-only view, before
 * the 1.5 s were up (until Nov 2026 that cancelled it, and the last edit was lost). 'drop': the draft was
 * submitted, and Submit saved everything itself.
 */
export function waitingAutosaveAction(
  waitingPrId: number,
  page: { prId: number | null; status: string; editable: boolean }
): 'keep' | 'send' | 'drop' {
  if (waitingPrId !== page.prId) return 'send'
  if (page.status !== 'Draft') return 'drop'
  return page.editable ? 'keep' : 'send'
}
